// @ts-check

/**
 * @file Renders the organization create/edit form.
 */

import {Form} from "@kaspernj/api-maker/build/form"
import Text from "@kaspernj/api-maker/build/utils/text"
import {useLocalSearchParams, useRouter} from "expo-router"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {createRef, memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {Switch, View} from "react-native"
import Button from "../../components/button"
import buildRoutePath from "../../shared/build-route-path"
import Card from "../../components/card"
import CountrySelect from "../../components/country-select"
import errorMessage from "../../shared/error-message"
import FormTextInput from "../../components/form/form-text-input"
import {Organization} from "../../frontend-models/organization"
import {optionalRouteParam} from "../../shared/route-params"
import useThemeColors, {useThemeName} from "../../shared/use-theme-colors"
import {useScreenLoading} from "../../components/board-layout/screen-loading"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{[key: string]: unknown}} OrganizationsEditScreenProps */

/**
 * @typedef {object} OrganizationsEditScreenState
 * @property {boolean} creditor Whether the organization is a creditor (controlled switch state).
 * @property {boolean} customer Whether the organization is a customer (controlled switch state).
 * @property {boolean} loading Whether organization data is loading.
 * @property {Organization | null} organization Loaded organization being edited.
 * @property {boolean} saving Whether form submission is running.
 * @property {{kind: "error" | "success", text: string} | null} status Screen status message.
 */

/**
 * Organization edit screen.
 * @augments {ShapeComponent<OrganizationsEditScreenProps, OrganizationsEditScreenState>}
 */
class OrganizationsEditScreen extends ShapeComponent {
  /** @type {string | null} */
  organizationID
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {ReturnType<typeof useRouter>} */
  router
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  formObjectRef = createRef()
  /** @type {OrganizationsEditScreenState} */
  state = {
    creditor: false,
    customer: false,
    loading: true,
    organization: /** @type {Organization | null} */ null,
    saving: false,
    status:
      /** @type {{kind: "error" | "success", text: string} | null} */ null
  }

  /** Initializes route params and organization loading. */
  setup() {
    useLocale()
    useScreenLoading(this.s.loading)
    const params = useLocalSearchParams()
    const router = useRouter()

    const organizationID = optionalRouteParam(params, "id", "Organization ID")
    this.organizationID = organizationID
    this.params = params
    this.router = router
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    useEffect(() => {
      let cancelled = false
      const loadData = async () => {
        try {
          if (organizationID) {
            const organization = await Organization.find(organizationID)
            if (cancelled) return
            this.s.customer = Boolean(organization.customer())
            this.s.creditor = Boolean(organization.creditor())
            this.s.loading = false
            this.s.organization = organization
          } else {
            this.s.customer = false
            this.s.creditor = false
            this.s.loading = false
          }
        } catch (error) {
          if (cancelled) return
          this.s.loading = false
          this.s.status = {
            kind: "error",
            text: errorMessage(error)
          }
        }
      }
      void loadData()
      return () => {
        cancelled = true
      }
    }, [organizationID])
  }

  /**
   * Renders the organization form.
   * @returns {ReactNode} Organization form content.
   */
  render() {
    const {creditor, customer, loading, organization, saving, status} = this.s
    const {organizationID} = this.tt
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    if (loading) {
      return (
        <View
          style={styles.loadingScreen ||= {flex: 1}}
          testID="organizationEditScreen"
        />
      )
    }
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="organizationEditScreen"
      >
        <View
          testID="organizationEditScreen/content"
        >
          <Text
            style={styles[`screenTitle-${themeName}`] ||= {
              fontSize: 24,
              fontWeight: 700,
              marginBottom: 16
            }}
            testID="organizationEditScreen/title"
          >
            {organizationID ? _("Edit organization") : _("New organization")}
          </Text>
          <Form formObjectRef={this.formObjectRef} onSubmit={this.tt.onSubmit}>
            <Card
              style={styles.formCardSpacing ||= {
                gap: 12,
                maxWidth: 520,
                width: "100%"
              }}
              testID="organizationEditScreen/form"
            >
              <FormTextInput
                defaultValue={organization ? organization.name() || "" : ""}
                label={_("Name")}
                name="name"
                testID="organizationEditScreen/nameInput"
              />
              <FormTextInput
                defaultValue={organization ? organization.email() || "" : ""}
                label={_("Email")}
                name="email"
                testID="organizationEditScreen/emailInput"
              />
              <FormTextInput
                defaultValue={organization ? organization.vatNo() || "" : ""}
                label={_("VAT no")}
                name="vatNo"
                testID="organizationEditScreen/vatNoInput"
              />
              <FormTextInput
                defaultValue={
                  organization ? organization.paymentInfo() || "" : ""
                }
                label={_("Payment info")}
                multiline
                name="paymentInfo"
                testID="organizationEditScreen/paymentInfoInput"
              />
              <View
                style={styles.switchRow ||= {
                  alignItems: "center",
                  flexDirection: "row",
                  gap: 8
                }}
                testID="organizationEditScreen/customerRow"
              >
                <Switch
                  onValueChange={this.tt.onCustomerChange}
                  testID="organizationEditScreen/customerSwitch"
                  value={customer}
                />
                <Text
                  style={styles[`switchLabel-${themeName}`] ||= {fontSize: 14}}
                  testID="organizationEditScreen/customerLabel"
                >
                  {_("Customer")}
                </Text>
              </View>
              <View
                style={styles.switchRow}
                testID="organizationEditScreen/creditorRow"
              >
                <Switch
                  onValueChange={this.tt.onCreditorChange}
                  testID="organizationEditScreen/creditorSwitch"
                  value={creditor}
                />
                <Text
                  style={styles[`switchLabel-${themeName}`]}
                  testID="organizationEditScreen/creditorLabel"
                >
                  {_("Creditor")}
                </Text>
              </View>
              <FormTextInput
                defaultValue={
                  organization ? organization.deliveryAddress() || "" : ""
                }
                label={_("Delivery address")}
                name="deliveryAddress"
                testID="organizationEditScreen/deliveryAddressInput"
              />
              <FormTextInput
                defaultValue={
                  organization ? organization.deliveryZipCode() || "" : ""
                }
                label={_("Delivery zip code")}
                name="deliveryZipCode"
                testID="organizationEditScreen/deliveryZipCodeInput"
              />
              <FormTextInput
                defaultValue={
                  organization ? organization.deliveryCity() || "" : ""
                }
                label={_("Delivery city")}
                name="deliveryCity"
                testID="organizationEditScreen/deliveryCityInput"
              />
              <CountrySelect
                defaultValue={
                  organization ? organization.deliveryCountry() || "" : ""
                }
                label={_("Delivery country")}
                name="deliveryCountry"
                testID="organizationEditScreen/deliveryCountryInput"
              />
              <FormTextInput
                defaultValue={
                  organization ? organization.invoiceAddress() || "" : ""
                }
                label={_("Invoice address")}
                name="invoiceAddress"
                testID="organizationEditScreen/invoiceAddressInput"
              />
              <FormTextInput
                defaultValue={
                  organization ? organization.invoiceZipCode() || "" : ""
                }
                label={_("Invoice zip code")}
                name="invoiceZipCode"
                testID="organizationEditScreen/invoiceZipCodeInput"
              />
              <FormTextInput
                defaultValue={
                  organization ? organization.invoiceCity() || "" : ""
                }
                label={_("Invoice city")}
                name="invoiceCity"
                testID="organizationEditScreen/invoiceCityInput"
              />
              <CountrySelect
                defaultValue={
                  organization ? organization.invoiceCountry() || "" : ""
                }
                label={_("Invoice country")}
                name="invoiceCountry"
                testID="organizationEditScreen/invoiceCountryInput"
              />
              {status &&
                <Text
                  style={styles[`status-${themeName}-${status.kind}`] ||= {
                    color: status.kind === "success" ? colors.successText : colors.dangerText,
                    fontWeight: 600
                  }}
                  testID="organizationEditScreen/statusMessage"
                >
                  {status.text}
                </Text>
              }
              <Button
                disabled={saving}
                iconName="floppy-disk"
                label={saving ? _("Saving...") : _("Save")}
                onPress={this.tt.onSubmit}
                testID="organizationEditScreen/saveButton"
              />
            </Card>
          </Form>
        </View>
      </View>
    )
  }

  /**
   * Toggles the customer flag.
   * @param {boolean} value New customer state.
   * @returns {void} Nothing.
   */
  onCustomerChange = (value) => {
    this.s.customer = value
  }

  /**
   * Toggles the creditor flag.
   * @param {boolean} value New creditor state.
   * @returns {void} Nothing.
   */
  onCreditorChange = (value) => {
    this.s.creditor = value
  }

  /**
   * Saves the organization form.
   * @returns {Promise<void>} Resolves when save handling completes.
   * @rejects {unknown} When save handling fails.
   */
  onSubmit = async () => {
    const {organizationID, params} = this.tt
    const {creditor, customer, organization} = this.s
    const formObject = this.formObjectRef.current
    if (!formObject) return
    const values = formObject.asObject()
    this.s.saving = true
    this.s.status = null
    try {
      const attributes = {
        creditor,
        customer,
        deliveryAddress: values.deliveryAddress || "",
        deliveryCity: values.deliveryCity || "",
        deliveryCountry: values.deliveryCountry || "",
        deliveryZipCode: values.deliveryZipCode || "",
        email: values.email || "",
        invoiceAddress: values.invoiceAddress || "",
        invoiceCity: values.invoiceCity || "",
        invoiceCountry: values.invoiceCountry || "",
        invoiceZipCode: values.invoiceZipCode || "",
        name: values.name || "",
        paymentInfo: values.paymentInfo || "",
        vatNo: values.vatNo || ""
      }
      if (organization) {
        await organization.update(attributes)
        this.tt.router.push(
          buildRoutePath(params, `/organizations/${organization.id()}`)
        )
      } else if (!organizationID) {
        const created = await Organization.create(attributes)
        this.tt.router.push(
          buildRoutePath(params, `/organizations/${created.id()}`)
        )
      }
    } catch (error) {
      this.s.saving = false
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
      throw error
    }
  }
}

/** @type {NamedExoticComponent<OrganizationsEditScreenProps>} */
const OrganizationsEditScreenComponent = memo(shapeComponent(OrganizationsEditScreen))

export default OrganizationsEditScreenComponent
